<script setup lang="ts">
  import { computed, ref, type Component } from 'vue';
  import { ChevronDown } from 'lucide-vue-next';
  import CheckMarkCircleIcon from '@/shared/assets/icons/CheckMarkCircleIcon.svg';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
  import OrderIcon from '@/shared/assets/icons/Order.svg';
  import {
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    ColoredButton,
    CustomCheckbox,
    IconImage,
    SearchBar,
  } from '@/shared/ui';
  import {
    permissionActionButtonClass,
    permissionHeaderIconClass,
    permissionSortButtonClass,
  } from './directoriesPermissions.styles';
  import {
    countAllowedEntries,
    filterPermissionEntries,
    formatSelectedCount,
    sortPermissionEntries,
  } from '../model/permissionSection.utils';
  import { PERMISSIONS_EMPTY_RESULTS_MESSAGE } from '../config/messages';
  import type { DirectoryPermissionEntry } from '../model/types';

  const props = withDefaults(
    defineProps<{
      value: string;
      title: string;
      headerIcon: Component;
      headerIconLabel: string;
      selectLabel: string;
      selectAllLabel: string;
      countLabel: string;
      entries: DirectoryPermissionEntry[];
      canApply?: boolean;
      canClear?: boolean;
    }>(),
    {
      canApply: false,
      canClear: false,
    }
  );

  const emit = defineEmits<{
    (e: 'toggle-entry', entryId: string): void;
    (e: 'toggle-select-all', entryIds: string[]): void;
    (e: 'apply'): void;
    (e: 'clear'): void;
  }>();

  const searchTerm = ref('');
  const sortAscending = ref(true);

  const filteredEntries = computed(() => {
    const filtered = filterPermissionEntries(props.entries, searchTerm.value);
    return sortPermissionEntries(filtered, sortAscending.value);
  });

  const selectedCount = computed(() => countAllowedEntries(props.entries));

  const formattedSelectedCount = computed(() => formatSelectedCount(selectedCount.value));

  const allVisibleSelected = computed(() => {
    if (filteredEntries.value.length === 0) {
      return false;
    }
    return filteredEntries.value.every((entry) => entry.allowed);
  });

  const selectAllChecked = computed(() => allVisibleSelected.value);

  const toggleSort = () => {
    sortAscending.value = !sortAscending.value;
  };

  const handleSearch = (term: string) => {
    searchTerm.value = term;
  };

  const toggleVisibleEntries = () => {
    emit(
      'toggle-select-all',
      filteredEntries.value.map((entry) => entry.id)
    );
  };
</script>

<template>
  <AccordionItem :value="value" class="rounded-lg border border-gray-02 bg-white-01">
    <AccordionTrigger
      class="border-b border-gray-02 px-3 py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180"
    >
      <div class="flex min-h-6 flex-1 items-center gap-2">
        <div :class="permissionHeaderIconClass">
          <component
            :is="headerIcon"
            class="size-3.5 shrink-0 text-white"
            :stroke-width="2.25"
            aria-hidden="true"
          />
        </div>
        <span class="text-sm font-semibold leading-none text-gray-07">{{ title }}</span>
      </div>
      <template #icon>
        <ChevronDown
          class="pointer-events-none size-4 shrink-0 text-[var(--theme-secondary-ink)] transition-transform duration-200"
        />
      </template>
    </AccordionTrigger>

    <AccordionContent class="px-3 pb-3 pt-3">
      <p class="mb-2 text-sm text-gray-07">
        {{ selectLabel }}
      </p>

      <div class="flex h-[42px] items-center gap-2">
        <SearchBar class="min-w-0 flex-1 !h-[42px]" @search="handleSearch" />
        <button
          type="button"
          :class="permissionSortButtonClass"
          aria-label="Ordenar lista"
          @click="toggleSort"
        >
          <IconImage :src="OrderIcon" alt="Ordenar" class="h-4 w-3.5 shrink-0" />
        </button>
      </div>

      <ul class="mt-3 max-h-36 overflow-y-auto rounded-lg border border-gray-02 py-1" role="list">
        <li
          v-if="filteredEntries.length > 0"
          class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-02"
        >
          <CustomCheckbox
            :id="`${value}-select-all`"
            :checked="selectAllChecked"
            @change="toggleVisibleEntries"
          />
          <label
            :for="`${value}-select-all`"
            class="cursor-pointer text-sm font-medium text-gray-07"
          >
            {{ selectAllLabel }}
          </label>
        </li>
        <li v-if="filteredEntries.length === 0" class="px-3 py-3 text-sm text-gray-05">
          {{ PERMISSIONS_EMPTY_RESULTS_MESSAGE }}
        </li>
        <li
          v-for="entry in filteredEntries"
          :key="entry.id"
          class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-02"
        >
          <CustomCheckbox
            :id="`${value}-${entry.id}`"
            :checked="entry.allowed"
            @change="emit('toggle-entry', entry.id)"
          />
          <label :for="`${value}-${entry.id}`" class="cursor-pointer text-sm text-gray-07">
            {{ entry.name }}
          </label>
        </li>
      </ul>

      <p class="mt-2 text-xs text-gray-05">{{ countLabel }} {{ formattedSelectedCount }}</p>

      <div class="mt-3 flex gap-2">
        <ColoredButton
          :class="permissionActionButtonClass"
          text="Aplicar"
          :icon="CheckMarkCircleIcon"
          icon-alt="Aplicar"
          :disabled="!canApply"
          @click="emit('apply')"
        />
        <ColoredButton
          :class="permissionActionButtonClass"
          text="Limpar"
          :icon="EraserIcon"
          icon-alt="Limpar"
          outlined
          color="[var(--theme-secondary-ink)]"
          border-color="[var(--theme-secondary-ink)]"
          :disabled="!canClear"
          @click="emit('clear')"
        />
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
